import { useEffect, useMemo, useState } from 'react';

import {
  AngleDown,
  AutoComplete,
  EquipUIFireflyV2Theme,
} from '@equip.health/ui';
import { useGetPayorsEnumQuery } from '~/store/services/enumerations';

type PayorSelectProps = {
  label?: string;
  onSelect: (label: string, id?: string, isUserSelected?: boolean) => void;
  selectedPayor: string;
};

const { primary } = EquipUIFireflyV2Theme.colors;

const PayorSelect = ({ label, onSelect, selectedPayor }: PayorSelectProps) => {
  const [selected, setSelected] = useState<AutoCompleteOption>();

  const { data: payors } = useGetPayorsEnumQuery();

  const filterOutInActivePayors = (payorObj: Payor[]): Payor[] =>
    payorObj.filter(({ isActive }: Payor): boolean => isActive);

  const activePayors = useMemo<Payor[]>(
    () => filterOutInActivePayors(payors ?? []),
    [payors],
  );
  const options = useMemo<AutoCompleteOption[]>(
    () =>
      activePayors.map(({ label: payorName, externalId }: Payor) => ({
        primaryText: payorName,
        value: externalId,
      })),
    [activePayors],
  );

  useEffect(() => {
    // When selectedPayor is defined (there is already a pre-existing payor), we want to trigger an
    // onSelect to populate the payor program in the parent component, so we need to make sure we have
    // all the payors fetched and find the UUID to send to the onSelect method. We can guarantee that
    // this path will only be taken on first load, because only then will selected be undefined, and
    // after we've successfully called onSelect, the selected state variable will no longer be null.
    if (!selected && payors?.length && selectedPayor) {
      const selectedUUID =
        payors.find(({ label: payorName }) => payorName === selectedPayor)
          ?.externalId ?? '';

      if (selectedUUID) {
        onSelect(selectedPayor, selectedUUID, false);
        setSelected({ primaryText: selectedPayor, value: selectedUUID });
      }
    }
  }, [selected, payors, selectedPayor]);

  const handlePayorSelect = (option: AutoCompleteOption): void => {
    const { primaryText, value } = option;
    setSelected(option);
    onSelect(primaryText, value);
  };

  return (
    <AutoComplete
      endIcon={<AngleDown fill={primary[100]} height="15px" width="15px" />}
      hideAvatar
      id="payor"
      isRequired
      isTextInput
      marginBottom="10px"
      onSelect={handlePayorSelect}
      options={options}
      placeholder="Select..."
      selected={
        selectedPayor
          ? [
              options.find(
                ({ primaryText }) => primaryText === selectedPayor,
              ) ?? selected,
            ]
          : []
      }
      title={label}
      toggleListOnFocus
    />
  );
};

export default PayorSelect;
